import React, { useState, FormEvent, useMemo, KeyboardEvent } from "react";
import { FileText, AlertCircle, AlertTriangle, Search, X, StickyNote, Package, User } from "lucide-react";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { APIError } from "src/errors";
import { PurchaseOrder } from "src/types/database";
import { WarehouseRoutingCheckIn } from "src/types/database/warehouseRoutingCheckIn";
import { TableColumn } from "src/types";
import { RoutingCheckInColumnHeader } from "src/enums";
import { WarehouseRoutingCheckInService } from "src/services/warehouseRoutingCheckIns";
import { PurchaseOrderService } from "src/services";
import { LoadingState, Table } from "src/components";
import { TableRowHighlight } from "src/components/ui/table";
import { TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { FormLvlErrorMessage } from "src/components/ui/feedback";
import { POWarehouseCheckInTooltip } from "src/components/tooltips/warehouseCheckInsTooltips";
import { RoutingInstructionCards } from "src/components/forms/euRelabelCheckIns/routingInstructionCards";
import { Button } from "src/shadcn/ui/button";
import { useAuthentication } from "src/context";
import { CHILD_PO_DEFAULT_SENDER_EMAIL } from "src/constants";

interface RoutingCheckInProductRow {
  id: string;
  name: string;
  skuName: string;
  barcode: string;
  size?: string;
  expectedQty: number;
  warehouseReceivedQty: number;
  previouslyReceived: number;
  remaining: number;
  receivedQty: string;
}

interface RoutingCheckInFormProps {
  purchaseOrder: PurchaseOrder;
  existingCheckIns: WarehouseRoutingCheckIn[];
  routingCheckInService: WarehouseRoutingCheckInService;
  purchaseOrderService: PurchaseOrderService;
}

const buildProductRows = (po: PurchaseOrder, checkIns: WarehouseRoutingCheckIn[]): RoutingCheckInProductRow[] => {
  const receivedByProductId = new Map<string, number>();
  for (const checkIn of checkIns) {
    for (const p of checkIn.products) {
      receivedByProductId.set(p.id, (receivedByProductId.get(p.id) ?? 0) + p.receivedQty);
    }
  }

  return (
    po.products?.map((poProduct) => {
      const confirmedQty = po.confirmationDetails?.products?.find((c) => c.productId === poProduct.id)?.confirmedQty;
      const expectedQty = confirmedQty ?? poProduct.quantity;
      const previouslyReceived = receivedByProductId.get(poProduct.id) ?? 0;
      const warehouseReceivedQty = poProduct.receivedQty ?? 0;
      const remaining = Math.max(0, expectedQty - previouslyReceived);
      return {
        id: poProduct.id,
        name: poProduct.name,
        skuName: poProduct.skuName ?? "—",
        barcode: poProduct.barcode ?? "—",
        size: (poProduct as any).size,
        expectedQty,
        warehouseReceivedQty,
        previouslyReceived,
        remaining,
        receivedQty: ""
      };
    }) ?? []
  );
};

const RoutingCheckInForm: React.FC<RoutingCheckInFormProps> = ({
  purchaseOrder,
  existingCheckIns: initialCheckIns,
  routingCheckInService,
  purchaseOrderService
}) => {
  const { userEmail } = useAuthentication();
  const [existingCheckIns, setExistingCheckIns] = useState<WarehouseRoutingCheckIn[]>(initialCheckIns);
  const [productRows, setProductRows] = useState<RoutingCheckInProductRow[]>(() =>
    buildProductRows(purchaseOrder, initialCheckIns)
  );
  const [productFilter, setProductFilter] = useState("");
  const [defects, setDefects] = useState("");
  const [newCheckIn, setNewCheckIn] = useState<WarehouseRoutingCheckIn>();
  const [noteSubmitted, setNoteSubmitted] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string>();
  const [formLvlError, setFormLvlError] = useState("");
  const [showOverageModal, setShowOverageModal] = useState(false);
  const [overageChildPOId, setOverageChildPOId] = useState<string>();
  const [overageChildPOError, setOverageChildPOError] = useState<string>();

  const handleReceivedQtyChange = (productId: string, value: string) => {
    setProductRows((prev) => prev.map((row) => (row.id === productId ? { ...row, receivedQty: value } : row)));
    setFormLvlError("");
  };

  const handleDefectsChange = (value: string) => {
    setDefects(value);
    setNoteSubmitted(false);
    setFormLvlError("");
  };

  const handleReceivedAll = () => {
    setProductRows((prev) => prev.map((row) => ({ ...row, receivedQty: row.remaining.toString() })));
  };

  const filteredProductRows = useMemo(() => {
    const term = productFilter.trim().toLowerCase();
    if (!term) return productRows;
    return productRows.filter((row) =>
      [row.id, row.name, row.skuName, row.barcode, row.size]
        .filter((v): v is string => typeof v === "string")
        .some((v) => v.toLowerCase().includes(term))
    );
  }, [productRows, productFilter]);

  const handleProductFilterKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (filteredProductRows.length === 1) {
      const onlyRow = filteredProductRows[0];
      const input = document.getElementById(`routing-recv-${onlyRow.id}`) as HTMLInputElement | null;
      input?.focus();
      input?.select();
    }
  };

  const hasAnyInput = productRows.some((r) => r.receivedQty && parseInt(r.receivedQty) > 0);
  const overageProducts = productRows.filter((r) => r.receivedQty && parseInt(r.receivedQty) > r.remaining);

  const refreshCheckIns = async (created: WarehouseRoutingCheckIn): Promise<WarehouseRoutingCheckIn[]> => {
    try {
      return await routingCheckInService.getAllByPurchaseOrderId(purchaseOrder.id);
    } catch {
      return [...existingCheckIns, created];
    }
  };

  const doSubmit = async () => {
    const products = productRows
      .filter((r) => r.receivedQty && parseInt(r.receivedQty) > 0)
      .map((r) => ({ id: r.id, receivedQty: parseInt(r.receivedQty) }));

    if (products.length === 0) {
      setFormLvlError("Enter a received quantity for at least one product");
      return;
    }

    const overageChildPOProducts = overageProducts.map((p) => ({
      id: p.id,
      ukQuantity: parseInt(p.receivedQty) - p.remaining,
      euQuantity: 0
    }));

    setLoadingMessage("Calculating Routing Instructions...");
    setFormLvlError("");
    setNoteSubmitted(false);
    setOverageChildPOId(undefined);
    setOverageChildPOError(undefined);

    try {
      const result = await routingCheckInService.createRoutingCheckIn(purchaseOrder.id, products, defects || undefined);
      const updatedCheckIns = await refreshCheckIns(result);
      setExistingCheckIns(updatedCheckIns);
      setNewCheckIn(updatedCheckIns.find((c) => c.id === result.id) ?? result);
      setProductRows(buildProductRows(purchaseOrder, updatedCheckIns));
      setDefects("");

      if (overageChildPOProducts.length > 0) {
        setLoadingMessage("Creating child PO for overage...");
        try {
          const childPO = await purchaseOrderService.createChildPurchaseOrder(purchaseOrder.id, {
            products: overageChildPOProducts,
            estDeliveryDate: new Date().toISOString(),
            operationsNote: `Auto-created child PO for overage during check-in by ${userEmail ?? "unknown user"}.`,
            useChildPODefaultSender: true,
            useChildConfirmationFlow: true
          });
          setOverageChildPOId(childPO.id);
        } catch (error) {
          const message = error instanceof APIError ? error.message : "Failed to auto-create child PO for overage.";
          setOverageChildPOError(message);
        }
      }
    } catch (error) {
      if (error instanceof APIError && error.type === "CLIENT_ERROR") {
        setFormLvlError(error.message);
      } else {
        setFormLvlError("An error occurred. Please try again.");
      }
    } finally {
      setLoadingMessage(undefined);
    }
  };

  const handleSubmit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (overageProducts.length > 0) {
      setShowOverageModal(true);
      return;
    }
    await doSubmit();
  };

  const handleSubmitNoteOnly = async () => {
    const trimmedDefects = defects.trim();
    if (trimmedDefects.length < 2) {
      setFormLvlError("Enter a note (at least 2 characters) before submitting.");
      return;
    }

    setLoadingMessage("Submitting note...");
    setFormLvlError("");
    setNoteSubmitted(false);
    setOverageChildPOId(undefined);
    setOverageChildPOError(undefined);

    try {
      const result = await routingCheckInService.createRoutingCheckIn(purchaseOrder.id, [], trimmedDefects);
      // Show the just-submitted note as the "new" entry at the top (it has no routing instructions,
      // so the routing cards render nothing). Use the server copy so the displayed note is authoritative.
      const updatedCheckIns = await refreshCheckIns(result);
      setExistingCheckIns(updatedCheckIns);
      setNewCheckIn(updatedCheckIns.find((c) => c.id === result.id) ?? result);
      setDefects("");
      setNoteSubmitted(true);
    } catch (error) {
      if (error instanceof APIError && error.type === "CLIENT_ERROR") {
        setFormLvlError(error.message);
      } else {
        setFormLvlError("An error occurred. Please try again.");
      }
    } finally {
      setLoadingMessage(undefined);
    }
  };

  if (loadingMessage) {
    return <LoadingState loadingMessage={loadingMessage} />;
  }

  const previousCheckIns = (newCheckIn ? existingCheckIns.filter((c) => c.id !== newCheckIn.id) : existingCheckIns)
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  const totalExpectedBoxesQty = purchaseOrder.shipmentUnits?.reduce(
    (total, unit) => total + (parseInt(unit.quantity.toString(), 10) || 0),
    0
  );

  const tableColumns: TableColumn<RoutingCheckInColumnHeader, RoutingCheckInProductRow>[] = [
    {
      header: RoutingCheckInColumnHeader.PRODUCT_ID,
      column: (row) => <span className="text-sm font-medium text-secondary-900">{row.id}</span>,
      styles: { minWidth: "120px" }
    },
    {
      header: RoutingCheckInColumnHeader.PRODUCT_NAME,
      column: (row) => <span className="text-sm text-secondary-700">{row.name}</span>,
      styles: { minWidth: "160px" }
    },
    {
      header: RoutingCheckInColumnHeader.PRODUCT_SKU,
      column: (row) => <span className="text-sm font-mono text-secondary-600">{row.skuName}</span>,
      styles: { minWidth: "100px" }
    },
    {
      header: RoutingCheckInColumnHeader.PRODUCT_BARCODE,
      column: (row) => <span className="text-sm font-mono text-secondary-600">{row.barcode}</span>,
      styles: { minWidth: "120px" }
    },
    {
      header: RoutingCheckInColumnHeader.SIZE,
      column: (row) => <span className="text-sm text-secondary-500">{row.size ?? "—"}</span>,
      styles: { minWidth: "80px" }
    },
    {
      header: RoutingCheckInColumnHeader.EXPECTED_QTY,
      column: (row) => <span className="text-sm text-secondary-700">{row.expectedQty}</span>,
      styles: { minWidth: "90px" }
    },
    {
      header: RoutingCheckInColumnHeader.WAREHOUSE_RECEIVED,
      column: (row) => <span className="text-sm text-secondary-500">{row.warehouseReceivedQty}</span>,
      styles: { minWidth: "130px" }
    },
    {
      header: RoutingCheckInColumnHeader.PREV_RECEIVED,
      column: (row) => <span className="text-sm text-secondary-500">{row.previouslyReceived}</span>,
      styles: { minWidth: "110px" }
    },
    {
      header: RoutingCheckInColumnHeader.RECEIVE_NOW,
      column: (row) => {
        const inputNum = parseInt(row.receivedQty) || 0;
        const isOver = inputNum > row.remaining;
        return (
          <div className="w-24">
            <TextInputBox
              id={`routing-recv-${row.id}`}
              value={row.receivedQty}
              onChange={(value) => handleReceivedQtyChange(row.id, value)}
              placeholder={`/ ${row.remaining}`}
              maxLength={10}
              size="sm"
            />
            {isOver && (
              <p className="text-xs text-amber-600 mt-0.5 whitespace-nowrap">+{inputNum - row.remaining} over</p>
            )}
          </div>
        );
      },
      styles: { minWidth: "120px" }
    }
  ];

  return (
    <div className="py-4 px-4 sm:px-5 lg:px-6">
      <div className="mx-auto">
        <form onSubmit={handleSubmit} autoComplete="off" className="p-4">
          <div className="space-y-4">
            <POWarehouseCheckInTooltip
              origin={purchaseOrder.origin}
              purchaseOrderId={purchaseOrder.id}
              totalExpectedBoxesQty={totalExpectedBoxesQty}
            />

            <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <h3 className="text-sm font-semibold text-black tracking-wide uppercase flex items-center shrink-0">
                  <FileText className="w-4 h-4 mr-2" />
                  Products
                </h3>
                <div className="relative flex-1 min-w-[240px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-500 pointer-events-none" />
                  <input
                    id="routing-product-filter"
                    type="text"
                    value={productFilter}
                    onChange={(e) => setProductFilter(e.target.value)}
                    onKeyDown={handleProductFilterKeyDown}
                    placeholder="Filter / scan barcode — product ID, name, SKU, barcode"
                    autoComplete="off"
                    className="w-full h-9 pl-10 pr-9 text-sm bg-white border border-secondary-200 rounded-lg placeholder:text-muted-400 focus:outline-none focus:ring-2 focus:ring-secondary-900 focus:border-secondary-900"
                  />
                  {productFilter && (
                    <button
                      type="button"
                      onClick={() => setProductFilter("")}
                      aria-label="Clear filter"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-400 hover:text-muted-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleReceivedAll}
                  className="text-xs px-3 py-1.5 bg-neutral-100 hover:bg-neutral-200 rounded-lg font-medium transition-colors shrink-0"
                >
                  Receive All Remaining
                </button>
              </div>
              {productFilter && (
                <p className="text-xs text-muted-600 mb-3">
                  Showing {filteredProductRows.length} of {productRows.length}
                </p>
              )}
              {filteredProductRows.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-600 border border-dashed border-neutral-200 rounded-lg">
                  No products match <span className="font-mono font-medium">"{productFilter}"</span>.
                </div>
              ) : (
                <Table
                  columns={tableColumns}
                  data={filteredProductRows}
                  rowHighlight={(row: RoutingCheckInProductRow): TableRowHighlight | undefined => {
                    const totalReceived = row.previouslyReceived + (parseInt(row.receivedQty) || 0);
                    if (totalReceived === 0) return undefined;
                    if (totalReceived > row.expectedQty) return "danger";
                    return totalReceived === row.expectedQty ? "success" : "info";
                  }}
                />
              )}
            </div>

            <div className="border border-neutral-200 rounded-2xl p-5 bg-white">
              <div className="flex items-center mb-4">
                <h3 className="text-sm font-semibold text-black tracking-wide uppercase flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Notes / Defects Report
                </h3>
              </div>
              <TextInputBoxLarge
                id="routing-defects"
                placeholder="Add a note or report defects — can be submitted on its own"
                onChange={handleDefectsChange}
                value={defects}
                maxLength={800}
              />
              {noteSubmitted && (
                <p className="mt-3 text-sm font-medium text-green-700">
                  Note submitted.
                </p>
              )}
            </div>

            <FormLvlErrorMessage message={formLvlError} centred={false} />

            {overageChildPOId && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-secondary-700">
                  Overage detected — child PO{" "}
                  <span className="font-mono font-semibold text-amber-700">{overageChildPOId}</span> was auto-created
                  for the excess quantity. It is in <span className="font-semibold">Draft</span> awaiting ops
                  confirmation before being sent to GXO. Yasin (
                  <a href={`mailto:${CHILD_PO_DEFAULT_SENDER_EMAIL}`} className="text-primary-600 underline">
                    {CHILD_PO_DEFAULT_SENDER_EMAIL}
                  </a>
                  ) has been notified.
                </p>
              </div>
            )}

            {overageChildPOError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-secondary-700">
                  <span className="font-medium text-green-700">Routing check-in succeeded</span>, but auto-creating the
                  overage child PO failed: <span className="font-medium text-red-700">{overageChildPOError}</span>.
                  Please contact Yasin (
                  <a href={`mailto:${CHILD_PO_DEFAULT_SENDER_EMAIL}`} className="text-primary-600 underline">
                    {CHILD_PO_DEFAULT_SENDER_EMAIL}
                  </a>
                  ) to raise a child PO manually.
                </p>
              </div>
            )}

            {newCheckIn && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-black uppercase tracking-wide">
                  {newCheckIn.routingInstructions.length > 0 ? "New Routing Instructions" : "Submitted Note"}
                </h3>
                {newCheckIn.defects && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <StickyNote className="w-4 h-4 text-amber-600 shrink-0" />
                      <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Note</p>
                    </div>
                    <p className="text-sm text-secondary-700 whitespace-pre-wrap break-words">{newCheckIn.defects}</p>
                  </div>
                )}
                <RoutingInstructionCards
                  routingInstructions={newCheckIn.routingInstructions}
                  variant="checkInRouting"
                />
              </div>
            )}

            <div className="flex items-center gap-4">
              {hasAnyInput ? (
                <Button type="submit" size="lg" className="bg-black text-white hover:bg-gray-800">
                  Calculate Routing Instructions
                </Button>
              ) : (
                <Button
                  type="button"
                  size="lg"
                  onClick={handleSubmitNoteOnly}
                  disabled={defects.trim().length < 2}
                  className="bg-black text-white hover:bg-gray-800"
                >
                  Submit Note
                </Button>
              )}
            </div>

            {previousCheckIns.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-neutral-500 uppercase tracking-wide">
                  Previous Routing Check-Ins ({previousCheckIns.length})
                </h3>
                {previousCheckIns.map((checkIn) => {
                  const isNoteOnly = checkIn.totalReceivedUnits === 0 && checkIn.routingInstructions.length === 0;
                  return (
                    <CollapsibleSection
                      key={checkIn.id}
                      title={
                        <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                          <span className="flex items-center gap-2 min-w-0">
                            {isNoteOnly ? (
                              <StickyNote className="w-4 h-4 text-amber-600 shrink-0" />
                            ) : (
                              <Package className="w-4 h-4 text-secondary-500 shrink-0" />
                            )}
                            <span className="text-sm font-medium text-secondary-800 truncate">
                              {new Date(checkIn.createdAt).toLocaleString()}
                            </span>
                          </span>
                          {isNoteOnly ? (
                            <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                              Note
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700">
                              {checkIn.totalReceivedUnits} units
                            </span>
                          )}
                          {checkIn.hasOverage && (
                            <span className="inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700">
                              Overage recorded
                            </span>
                          )}
                          {checkIn.submittedByEmail && (
                            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-muted-600 min-w-0">
                              <User className="w-3 h-3 shrink-0" />
                              <span className="truncate">{checkIn.submittedByEmail}</span>
                            </span>
                          )}
                        </span>
                      }
                      variant="default"
                    >
                      <div className="px-4 sm:px-6 pb-5 pt-2 space-y-4">
                        {checkIn.defects && (
                          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <StickyNote className="w-4 h-4 text-amber-600 shrink-0" />
                              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                                {isNoteOnly ? "Note" : "Defects Report"}
                              </p>
                            </div>
                            <p className="text-sm text-secondary-700 whitespace-pre-wrap break-words">
                              {checkIn.defects}
                            </p>
                          </div>
                        )}
                        {checkIn.routingInstructions.length > 0 && (
                          <RoutingInstructionCards
                            routingInstructions={checkIn.routingInstructions}
                            variant="checkInRouting"
                          />
                        )}
                      </div>
                    </CollapsibleSection>
                  );
                })}
              </div>
            )}
          </div>
        </form>
      </div>

      {showOverageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-secondary-900 text-base">Quantity Exceeds Expected</h3>
                <p className="text-sm text-secondary-600 mt-1">
                  One or more products exceed the expected remaining quantity:
                </p>
                <ul className="mt-2 space-y-1">
                  {overageProducts.map((p) => (
                    <li key={p.id} className="text-sm">
                      <span className="font-mono font-semibold">{p.id}</span>
                      <span className="text-secondary-600">
                        {" "}
                        — received {p.receivedQty}, remaining {p.remaining} (+
                        {parseInt(p.receivedQty) - p.remaining} over)
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-secondary-700 mt-3 font-medium">
                  A child PO will be automatically created in <span className="font-semibold">Draft</span> for the
                  excess quantity once you continue. The extra units will be processed here. Yasin (
                  <a href={`mailto:${CHILD_PO_DEFAULT_SENDER_EMAIL}`} className="text-primary-600 underline">
                    {CHILD_PO_DEFAULT_SENDER_EMAIL}
                  </a>
                  ) will be notified to review and confirm the child PO before it is sent to GXO.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowOverageModal(false)}
                className="border-neutral-200 text-secondary-700 hover:bg-neutral-50"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowOverageModal(false);
                  doSubmit();
                }}
                className="bg-amber-500 text-white hover:bg-amber-600"
              >
                Process Anyway
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { RoutingCheckInForm };
